import { useState, useEffect, useMemo } from "react";

// Importing formik & yup
import { Formik } from "formik";
import * as Yup from "yup";

// Importing bootstrap components
import { Col, Card, Form, Button, Spinner, Row } from "react-bootstrap";

import { useSelector, useDispatch } from "react-redux";

// User Query
import { setEmployees } from "state/slices/user/userSlice";

import { useGetUsersQuery } from "state/slices/user/userApiSlice";

// Import react-select
import Select from "react-select";

// Import utilities
import { showSuccessToast } from "utils/toast";

const actionOptions = [
  { value: "", label: "Select action" },
  { value: "UPDATE", label: "Update" },
  { value: "DELETE", label: "Delete" },
];

const PayrollNotes = ({ updatePayrollNotes, deletePayrollNotes }) => {
  const employees = useSelector((state) => state.employee.employees);
  const employeesArray = useMemo(() => {
    return employees.map((employee) => ({
      value: employee.employeeId,
      label: employee.name,
    }));
  }, [employees]);

  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  const lastMonthStr = lastMonth.toISOString().substr(0, 7);

  const dispatch = useDispatch();
  const { data: users } = useGetUsersQuery();

  useEffect(() => {
    if (users) {
      dispatch(setEmployees(users));
    }
  }, [users, dispatch]);

  const validationSchema = Yup.object().shape({
    action: Yup.object()
      .shape({
        value: Yup.string().required("Please select an action"),
      })
      .required("Please select an action"),
    selectedEmployees: Yup.array()
      .min(1, "Please select at least one employee")
      .of(Yup.object())
      .required("Please select at least one employee"),
    year_month: Yup.string().required("Please select a month"),
    note: Yup.string()
      .when("action", {
        is: (action) => action && action.value !== "DELETE",
        then: (schema) =>
          schema
            .required("Please enter a note")
            .trim(),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

  return (
    <Col md={12}>
      <Card className="mb-3">
        <Card.Body>
          <Formik
            enableReinitialize={true}
            initialValues={{
              action: actionOptions[0],
              selectedEmployees: [],
              note: "",
              year_month: lastMonthStr,
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                const payload = {
                  employees: values.selectedEmployees.map(
                    (employee) => employee.value
                  ),
                  year: parseInt(values.year_month.split("-")[0], 10),
                  month: parseInt(values.year_month.split("-")[1], 10),
                };

                if (values.action.value === "UPDATE") {
                  payload.note = values.note;
                  await updatePayrollNotes(payload).unwrap();
                  showSuccessToast("Payroll notes updated successfully");
                }

                if (values.action.value === "DELETE") {
                  await deletePayrollNotes(payload).unwrap();
                  showSuccessToast("Payroll notes deleted successfully");
                }

                resetForm();
              } catch (err) {
                // Error toast is automatically shown by baseQuery
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              handleChange,
              handleSubmit,
              touched,
              errors,
              setFieldValue,
              isSubmitting,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Row className="my-3">
                  <Col md={3} className="mb-3 mb-md-0">
                    <Form.Group controlId="action">
                      <Select
                        name="action"
                        options={actionOptions}
                        value={values.action}
                        onChange={(selectedOption) => {
                          setFieldValue("action", selectedOption);
                        }}
                        classNamePrefix="select"
                        isInvalid={touched.action && !!errors.action}
                      />
                      {touched.action && errors.action && (
                        <div className="invalid-feedback d-block">
                          {errors.action.value}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={4} className="mb-3 mb-md-0">
                    <Form.Group controlId="selectedEmployees">
                      <Select
                        name="selectedEmployees"
                        options={employeesArray}
                        value={values.selectedEmployees}
                        onChange={(selectedOptions) => {
                          setFieldValue("selectedEmployees", selectedOptions);
                        }}
                        isMulti
                        className={
                          touched.selectedEmployees && errors.selectedEmployees
                            ? "is-invalid"
                            : ""
                        }
                      />
                      {touched.selectedEmployees && errors.selectedEmployees && (
                        <div className="invalid-feedback d-block">
                          {errors.selectedEmployees}
                        </div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={3} className="mb-3 mb-md-0">
                    <Form.Group controlId="year_month">
                      <Form.Control
                        type="month"
                        name="year_month"
                        value={values.year_month}
                        onChange={handleChange}
                        isInvalid={touched.year_month && !!errors.year_month}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.year_month}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Button
                      variant="primary"
                      className="w-100"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <Spinner
                          animation="border"
                          size="sm"
                          role="status"
                          style={{ width: "18px", height: "18px" }}
                        />
                      ) : 
                        "Apply"
                      }
                    </Button>
                  </Col>
                </Row>
                <Form.Group controlId="note">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="note"
                    value={values.action && values.action.value === "DELETE" ? "" : values.note}
                    onChange={handleChange}
                    isInvalid={touched.note && !!errors.note}
                    disabled={values.action && values.action.value === "DELETE"}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.note}
                  </Form.Control.Feedback>
                </Form.Group>
              </Form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default PayrollNotes;
